import { jsxs, jsx } from "react/jsx-runtime";
import assert from "assert";
import { Children } from "react";
import { flattenChildren } from "../../utils/flattenChildren.mjs";
import { buildDataAttributes } from "../private/buildDataAttributes.mjs";
import { Box } from "../Box/Box.mjs";
import { Text } from "../Text/Text.mjs";
import { Inline } from "../Inline/Inline.mjs";
import { Stack } from "../Stack/Stack.mjs";
import { MenuRenderer } from "../MenuRenderer/MenuRenderer.mjs";
import { MenuItemStepper } from "../private/MenuItemStepper/MenuItemStepper.mjs";
import "../Text/TextContext.mjs";
import "../Heading/HeadingContext.mjs";
import "../../css/lineHeightContainer.css.mjs";
import "../../css/reset/reset.css.mjs";
import "../../css/atoms/sprinkles.css.mjs";
import "../../hooks/useIcon/icon.css.mjs";
import "../../css/typography.css.mjs";
import { IconChevronDown } from "../icons/IconChevronDown/IconChevronDown.mjs";
import { IconChevronUp } from "../icons/IconChevronUp/IconChevronUp.mjs";
import { ResponsiveRender } from "../ResponsiveRender/ResponsiveRender.mjs";
import { StepperContextProvider, StepContext } from "./StepperContext.mjs";
import { ProgressRing } from "../private/ProgressRing/ProgressRing.mjs";
import { progressTrack, stepperProgress, stretchLastAboveTablet, stretch } from "./Stepper.css.mjs";
const Wrapper = ({ children }) => /* @__PURE__ */ jsx("div", { children });
Wrapper.__isMenuItem__ = true;
const resolveActiveStep = (mode, progress, activeStep) => {
  if (mode === "linear" && (typeof activeStep === "undefined" || activeStep > progress)) {
    return progress;
  }
  return activeStep || 0;
};
const stroke = 5;
const ringSize = 45;
const Stepper = ({
  direction = "vertical",
  activeStep,
  label,
  mode = "linear",
  tone,
  children,
  data,
  id,
  onStepClick,
  stepCountLabel,
  nextLabel,
  align = "left",
  showProgress = false,
  showProgressText,
  percentage,
  ...restProps
}) => {
  const steps = flattenChildren(children);
  const stepCount = steps.length;
  const isLinear = mode === "linear";
  const isVertical = direction === "vertical";
  const isLeft = align === "left";
  const progress = "progress" in restProps ? restProps.progress : 0;
  const activeStepNumber = resolveActiveStep(mode, progress, activeStep);
  const hasPercentage = percentage !== void 0;
  const isLastStep = activeStepNumber === stepCount;
  let stepName = "";
  let nextStepName = "";
  assert(
    !hasPercentage || percentage >= 0 && percentage <= 100,
    "Percentage must be a number between 0 and 100."
  );
  const stepItems = Children.map(steps, (child, index) => {
    const stepNumber = index + 1;
    const isLast = stepNumber === stepCount;
    assert(
      !child || child.type.__isStep__,
      "Only Step elements can be direct children of a Stepper. See the documentation for correct usage: https://design-system.machobear.ca"
    );
    if (stepNumber === activeStepNumber && typeof child === "object") {
      stepName = child.props.label;
    }
    if (stepNumber === activeStepNumber + 1 && typeof child === "object") {
      nextStepName = child.props.label;
    }
    return { stepNumber, isLast, child };
  });
  const percentageProgress = hasPercentage ? percentage : activeStepNumber / stepCount * 100;
  const percentageText = hasPercentage ? `${percentage}%` : showProgress ? `${percentageProgress}%` : `${activeStepNumber} ${stepCountLabel} ${stepCount}`;
  const radius = ringSize / 2 + stroke * 2.5;
  return /* @__PURE__ */ jsxs(
    StepperContextProvider,
    {
      activeStep: activeStepNumber,
      tone,
      progress,
      stepCount,
      isLinear,
      onStepClick,
      direction,
      children: [
        /* @__PURE__ */ jsx(ResponsiveRender, { below: "tablet", children: /* @__PURE__ */ jsxs(
          Box,
          {
            component: "nav",
            position: "relative",
            "aria-label": label,
            id,
            width: "full",
            ...buildDataAttributes({ data, validateRestProps: restProps }),
            children: [
              isLinear ? /* @__PURE__ */ jsxs(Inline, { space: "medium", alignY: "center", children: [
                /* @__PURE__ */ jsx(ProgressRing, { progress: percentageProgress, radius, stroke, children: /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(Text, { weight: "strong", size: "xsmall", children: percentageText }) }) }),
                /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
                  /* @__PURE__ */ jsx(Text, { weight: "strong", children: stepName }),
                  !isLastStep && isLinear ? /* @__PURE__ */ jsx(Text, { size: "small", tone: "tertiary", children: `${nextLabel} ${nextStepName}` }) : null
                ] })
              ] }) : /* @__PURE__ */ jsx(
                MenuRenderer,
                {
                  offsetSpace: "small",
                  trigger: (triggerProps, { open }) => /* @__PURE__ */ jsx(Box, { userSelect: "none", cursor: "pointer", width: "full", ...triggerProps, children: /* @__PURE__ */ jsxs(
                    Box,
                    {
                      gap: "medium",
                      display: "flex",
                      justifyContent: "spaceBetween",
                      width: "full",
                      alignItems: "center",
                      children: [
                        /* @__PURE__ */ jsx(ProgressRing, { progress: percentageProgress, radius, stroke, children: /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(Text, { weight: "strong", size: "xsmall", children: `${percentageText}` }) }) }),
                        /* @__PURE__ */ jsxs(
                          Box,
                          {
                            display: "flex",
                            justifyContent: "spaceBetween",
                            width: "full",
                            alignItems: "center",
                            children: [
                              /* @__PURE__ */ jsx(Text, { weight: "strong", children: stepName }),
                              open ? /* @__PURE__ */ jsx(IconChevronUp, {}) : /* @__PURE__ */ jsx(IconChevronDown, {})
                            ]
                          }
                        )
                      ]
                    }
                  ) }),
                  children: stepItems.map((stepItem, index) => /* @__PURE__ */ jsx(Wrapper, { children: /* @__PURE__ */ jsx(
                    StepContext.Provider,
                    {
                      value: {
                        stepNumber: stepItem.stepNumber,
                        isLast: stepItem.isLast
                      },
                      children: /* @__PURE__ */ jsx(MenuItemStepper, { ...stepItem.child.props })
                    }
                  ) }, `MenuStep-${index}`))
                }
              ),
              isLinear ? /* @__PURE__ */ jsx(
                Box,
                {
                  component: "span",
                  position: "absolute",
                  pointerEvents: "none",
                  className: progressTrack,
                  role: "progressbar",
                  "aria-valuemin": 0,
                  "aria-valuemax": stepCount - 1,
                  "aria-valuenow": progress > 0 ? progress - 1 : void 0,
                  "aria-valuetext": progress > 0 ? label : void 0,
                  style: {
                    left: `${(100 - (stepCount - 1) / stepCount * 100) / 2}%`,
                    width: `${(progress - 1) / stepCount * 100}%`
                  }
                }
              ) : null
            ]
          }
        ) }),
        /* @__PURE__ */ jsx(ResponsiveRender, { above: "mobile", children: /* @__PURE__ */ jsx(
          Box,
          {
            component: "nav",
            position: "relative",
            "aria-label": label,
            id,
            width: "full",
            ...buildDataAttributes({ data, validateRestProps: restProps }),
            children: /* @__PURE__ */ jsxs(
              Box,
              {
                gap: "gutter",
                display: "flex",
                flexDirection: isVertical ? "column" : "row",
                alignItems: isVertical ? "flexStart" : "center",
                width: "full",
                children: [
                  showProgress && isVertical && /* @__PURE__ */ jsx(Box, { data: { testid: "stepper-progress" }, className: stepperProgress, children: /* @__PURE__ */ jsx(Text, { weight: "strong", children: `${percentageProgress}% ${showProgressText}` }) }),
                  /* @__PURE__ */ jsx(
                    Box,
                    {
                      component: "ol",
                      display: "flex",
                      flexDirection: direction === "horizontal" ? "row" : "column",
                      paddingBottom: { mobile: "medium", tablet: "none" },
                      justifyContent: isVertical ? "flexStart" : "center",
                      alignItems: isLeft ? "flexStart" : "center",
                      width: "full",
                      data: { testid: "desktop-stepper" },
                      children: stepItems.map((stepItem) => /* @__PURE__ */ jsx(
                        StepContext.Provider,
                        {
                          value: {
                            stepNumber: stepItem.stepNumber,
                            isLast: stepItem.isLast
                          },
                          children: /* @__PURE__ */ jsx(
                            Box,
                            {
                              component: "li",
                              className: stepItem.isLast ? stretchLastAboveTablet : stretch,
                              children: stepItem.child
                            }
                          )
                        },
                        stepItem.stepNumber
                      ))
                    }
                  ),
                  isLinear ? /* @__PURE__ */ jsx(
                    Box,
                    {
                      component: "span",
                      position: "absolute",
                      pointerEvents: "none",
                      className: progressTrack,
                      role: "progressbar",
                      "aria-valuemin": 0,
                      "aria-valuemax": stepCount - 1,
                      "aria-valuenow": progress > 0 ? progress - 1 : void 0,
                      "aria-valuetext": progress > 0 ? label : void 0,
                      style: {
                        left: `${(100 - (stepCount - 1) / stepCount * 100) / 2}%`,
                        width: `${(progress - 1) / stepCount * 100}%`
                      }
                    }
                  ) : null,
                  showProgress && !isVertical && /* @__PURE__ */ jsx(Box, { data: { testid: "stepper-progress" }, className: stepperProgress, children: /* @__PURE__ */ jsx(Text, { weight: "strong", children: `${percentageProgress}% ${showProgressText}` }) })
                ]
              }
            )
          }
        ) })
      ]
    }
  );
};
export {
  Stepper
};
